if (!customElements.get("fourpaw-size-finder")) {
  customElements.define(
    "fourpaw-size-finder",
    class FourPawSizeFinder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.unit = this.dataset.defaultUnit === "kg" ? "kg" : "lb";
        this.sizeOptionName = this.normalize(this.dataset.sizeOptionName || "Size");
        this.guide = this.readGuide();
        this.weightInput = this.querySelector("[data-size-weight]");
        this.chestInput = this.querySelector("[data-size-chest]");
        this.unitLabel = this.querySelector("[data-size-unit-label]");
        this.chestUnitLabel = this.querySelector("[data-size-chest-unit-label]");
        this.result = this.querySelector("[data-size-result]");
        this.resultSize = this.querySelector("[data-size-result-size]");
        this.resultCopy = this.querySelector("[data-size-result-copy]");
        this.recommendedSize = "";
        this.canApplyRecommendation = false;

        this.querySelectorAll("[data-size-unit]").forEach((button) => {
          button.addEventListener("click", () => this.setUnit(button.dataset.sizeUnit));
        });
        this.querySelector("[data-size-calculate]")?.addEventListener("click", () => this.calculate());
        this.querySelector("[data-size-apply]")?.addEventListener("click", () => this.applySize());
        [this.weightInput, this.chestInput].forEach((input) => {
          input?.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              this.calculate();
            }
          });
          input?.addEventListener("blur", () => this.roundInput(input));
        });

        this.setUnit(this.unit, false);
      }

      normalize(value) {
        return String(value || "").trim().toLowerCase();
      }

      parseRange(label, unit) {
        const escapedUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const pattern = new RegExp(
          `(\\d+(?:\\.\\d+)?)\\s*[–—-]\\s*(\\d+(?:\\.\\d+)?)\\s*${escapedUnit}\\b`,
          "i",
        );
        const match = String(label || "").match(pattern);
        if (!match) return null;
        const min = Number(match[1]);
        const max = Number(match[2]);
        if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
        return { min, max };
      }

      readGuide() {
        const source = this.querySelector("[data-size-guide]");
        if (!source) return [];

        try {
          const rows = JSON.parse(source.textContent || "[]");
          if (!Array.isArray(rows)) return [];

          return rows
            .map((row) => {
              const weight = String(row?.weight || "").trim();
              const chest = String(row?.chest || "").trim();
              const minLb = Number(row?.minLb);
              const maxLb = Number(row?.maxLb);
              return {
                size: String(row?.size || "").trim(),
                weight,
                chest,
                weightLb: { min: minLb, max: maxLb },
                weightKg: this.parseRange(weight, "kg"),
                chestIn: this.parseRange(chest, "in"),
                chestCm: this.parseRange(chest, "cm"),
              };
            })
            .filter(
              (row) =>
                row.size &&
                Number.isFinite(row.weightLb.min) &&
                Number.isFinite(row.weightLb.max) &&
                row.weightLb.min <= row.weightLb.max &&
                row.weightKg &&
                row.chestIn &&
                row.chestCm,
            )
            .sort(
              (left, right) =>
                left.weightLb.min - right.weightLb.min ||
                left.weightLb.max - right.weightLb.max,
            );
        } catch (error) {
          console.warn("FourPaw size guide data could not be read.", error);
          return [];
        }
      }

      wholeNumber(value) {
        const number = Number(value);
        return Number.isFinite(number) ? Math.round(number) : 0;
      }

      roundInput(input) {
        if (!input || input.value === "") return 0;
        const rounded = this.wholeNumber(input.value);
        input.value = rounded > 0 ? String(rounded) : "";
        return rounded;
      }

      setUnit(unit, convert = true) {
        const nextUnit = unit === "kg" ? "kg" : "lb";
        if (convert && nextUnit !== this.unit) {
          this.convertInput(this.weightInput, nextUnit === "kg" ? 1 / 2.2046226218 : 2.2046226218);
          this.convertInput(this.chestInput, nextUnit === "kg" ? 2.54 : 1 / 2.54);
        }

        this.unit = nextUnit;
        if (this.unitLabel) this.unitLabel.textContent = nextUnit;
        if (this.chestUnitLabel) this.chestUnitLabel.textContent = nextUnit === "kg" ? "cm" : "in";
        if (this.weightInput) {
          this.weightInput.min = "1";
          this.weightInput.max = nextUnit === "kg" ? "180" : "400";
          this.weightInput.step = "1";
        }
        if (this.chestInput) {
          this.chestInput.min = "1";
          this.chestInput.max = nextUnit === "kg" ? "200" : "80";
          this.chestInput.step = "1";
        }

        this.querySelectorAll("[data-size-unit]").forEach((button) => {
          button.setAttribute("aria-pressed", button.dataset.sizeUnit === nextUnit ? "true" : "false");
        });
      }

      convertInput(input, factor) {
        if (!input || input.value === "") return;
        const currentValue = Number(input.value);
        if (!Number.isFinite(currentValue) || currentValue <= 0) return;
        input.value = String(Math.round(currentValue * factor));
      }

      message(name, fallback) {
        return this.dataset[name] || fallback;
      }

      interpolate(template, values) {
        return Object.entries(values).reduce(
          (copy, [token, value]) => copy.split(`[${token}]`).join(String(value ?? "")),
          String(template || ""),
        );
      }

      rangeFor(row, kind) {
        if (kind === "chest") return this.unit === "kg" ? row.chestCm : row.chestIn;
        return this.unit === "kg" ? row.weightKg : row.weightLb;
      }

      recommendation(value, kind) {
        const matches = this.guide.filter((row) => {
          const range = this.rangeFor(row, kind);
          return range && value >= range.min && value <= range.max;
        });
        if (matches.length === 1) return { type: "single", row: matches[0], kind };
        if (matches.length > 1) return { type: "overlap", rows: matches, kind };

        const lower = [...this.guide]
          .filter((row) => this.rangeFor(row, kind)?.max < value)
          .sort(
            (left, right) =>
              this.rangeFor(right, kind).max - this.rangeFor(left, kind).max,
          )[0];
        const upper = this.guide.find((row) => this.rangeFor(row, kind)?.min > value);
        if (lower && upper) return { type: "gap", lower, upper, kind };
        if (lower) return { type: "above", row: lower, kind };
        if (upper) return { type: "below", row: upper, kind };
        return { type: "unavailable", kind };
      }

      rowsForRecommendation(recommendation) {
        if (!recommendation) return [];
        if (recommendation.type === "single") return [recommendation.row];
        if (recommendation.type === "overlap") return recommendation.rows;
        if (recommendation.type === "gap") {
          return [recommendation.lower, recommendation.upper];
        }
        if (recommendation.row) return [recommendation.row];
        return [];
      }

      sameSize(left, right) {
        return this.normalize(left?.size) === this.normalize(right?.size);
      }

      resolveWeightFirst(weightRecommendation, chestRecommendation) {
        const chestRows = this.rowsForRecommendation(chestRecommendation);

        if (weightRecommendation.type === "single") {
          if (!chestRecommendation) return weightRecommendation;
          if (chestRows.some((row) => this.sameSize(row, weightRecommendation.row))) {
            return { ...weightRecommendation, chestConfirmed: true };
          }
          if (chestRows.length) {
            return {
              type: "conflict",
              weightRow: weightRecommendation.row,
              chestRows,
              kind: "weight",
            };
          }
          return weightRecommendation;
        }

        if (weightRecommendation.type === "overlap") {
          const matchingRows = chestRows.length
            ? weightRecommendation.rows.filter((weightRow) =>
                chestRows.some((chestRow) => this.sameSize(weightRow, chestRow)),
              )
            : [];
          if (matchingRows.length === 1) {
            return {
              type: "single",
              row: matchingRows[0],
              kind: "weight",
              resolvedByChest: true,
            };
          }
          if (chestRows.length && !matchingRows.length) {
            return {
              type: "conflict",
              weightRow: weightRecommendation.rows.at(-1),
              chestRows,
              kind: "weight",
            };
          }
          return {
            type: "boundary",
            row: weightRecommendation.rows.at(-1),
            smaller: weightRecommendation.rows[0],
            kind: "weight",
          };
        }

        if (weightRecommendation.type === "gap") {
          const candidates = [weightRecommendation.lower, weightRecommendation.upper];
          const matchingRows = chestRows.length
            ? candidates.filter((weightRow) =>
                chestRows.some((chestRow) => this.sameSize(weightRow, chestRow)),
              )
            : [];
          if (matchingRows.length === 1) {
            return {
              type: "single",
              row: matchingRows[0],
              kind: "weight",
              resolvedByChest: true,
            };
          }
          if (chestRows.length && !matchingRows.length) {
            return {
              type: "conflict",
              weightRow: weightRecommendation.upper,
              chestRows,
              kind: "weight",
            };
          }
          return {
            type: "gapChoice",
            row: weightRecommendation.upper,
            other: weightRecommendation.lower,
            kind: "weight",
          };
        }

        return weightRecommendation;
      }

      calculate() {
        const weight = this.roundInput(this.weightInput);
        const chest = this.roundInput(this.chestInput);
        if (!weight && !chest) {
          this.clearRecommendation();
          this.showMessage(
            this.message(
              "emptyMessage",
              "Enter your dog’s current or expected adult weight to get a starting size.",
            ),
            "",
          );
          return;
        }

        const recommendation = weight
          ? this.resolveWeightFirst(
              this.recommendation(weight, "weight"),
              chest ? this.recommendation(chest, "chest") : null,
            )
          : this.recommendation(chest, "chest");
        this.renderRecommendation(recommendation);
      }

      clearRecommendation() {
        this.recommendedSize = "";
        this.canApplyRecommendation = false;
      }

      renderRecommendation(recommendation) {
        this.clearRecommendation();

        if (recommendation.type === "single") {
          const { row } = recommendation;
          this.recommendedSize = row.size;
          this.canApplyRecommendation = true;
          const copy =
            recommendation.resolvedByChest
              ? this.interpolate(
                  this.message(
                    "resolvedMessage",
                    "Weight places your dog at a size boundary, and the optional chest check points to [size].",
                  ),
                  { size: row.size, chest: row.chest },
                )
              : recommendation.kind === "chest"
              ? this.interpolate(
                  this.message(
                    "chestMessage",
                    "Chest girth points to [size]. Weight is the quickest starting point when it is available.",
                  ),
                  { size: row.size, chest: row.chest },
                )
              : this.interpolate(
                  this.message(
                    "singleMessage",
                    "We recommend [size] based on weight. No measuring is normally needed.",
                  ),
                  { size: row.size, chest: row.chest },
                );
          this.showMessage(copy, row.size);
          return;
        }

        if (recommendation.type === "boundary") {
          const { row, smaller } = recommendation;
          this.recommendedSize = row.size;
          this.canApplyRecommendation = true;
          this.showMessage(
            this.interpolate(
              this.message(
                "overlapMessage",
                "[size] is the easier starting choice at this weight. If your dog is fully grown and compact, [smaller] may fit better. Measure only if you are unsure, or enter the weight you expect a growing dog to reach.",
              ),
              { size: row.size, smaller: smaller.size, chest: row.chest },
            ),
            row.size,
          );
          return;
        }

        if (recommendation.type === "gapChoice") {
          const { row, other } = recommendation;
          this.recommendedSize = row.size;
          this.canApplyRecommendation = true;
          this.showMessage(
            this.interpolate(
              this.message(
                "gapMessage",
                "[size] is the practical next size for this weight. If your dog is still growing, use the weight you expect them to reach. Measure only if you are unsure.",
              ),
              { size: row.size, other: other.size, chest: row.chest },
            ),
            row.size,
          );
          return;
        }

        if (recommendation.type === "conflict") {
          const chestSizes = recommendation.chestRows.map((row) => row.size).join(" / ");
          this.showMessage(
            this.interpolate(
              this.message(
                "conflictMessage",
                "Weight suggests [size], while the optional chest measurement suggests [chestSize]. Because the two checks disagree, compare the chart before choosing.",
              ),
              { size: recommendation.weightRow.size, chestSize: chestSizes },
            ),
            `${recommendation.weightRow.size} / ${chestSizes}`,
          );
          return;
        }

        if (recommendation.type === "overlap") {
          const sizes = recommendation.rows.map((row) => row.size).join(" / ");
          const ranges = recommendation.rows
            .map((row) => `${row.size}: ${row.chest}`)
            .join("; ");
          this.showMessage(
            this.interpolate(
              this.message(
                "overlapMessage",
                "Chest girth fits more than one size: [sizes]. Compare [ranges] or use weight for the quickest recommendation.",
              ),
              { sizes, ranges },
            ),
            sizes,
          );
          return;
        }

        if (recommendation.type === "gap") {
          this.showMessage(
            this.interpolate(
              this.message(
                "gapMessage",
                "Chest girth sits between [lower] and [upper]. Use weight for the quickest recommendation or compare both ranges.",
              ),
              { lower: recommendation.lower.size, upper: recommendation.upper.size },
            ),
            `${recommendation.lower.size} / ${recommendation.upper.size}`,
          );
          return;
        }

        if (recommendation.type === "above") {
          const { row, kind } = recommendation;
          const range = this.rangeFor(row, kind);
          const softLimit = Math.round(range.max * 1.1);
          const slightlyAbove =
            recommendation.kind === "weight" &&
            this.wholeNumber(this.weightInput?.value) <= softLimit;
          this.recommendedSize = row.size;
          this.canApplyRecommendation = slightlyAbove;
          this.showMessage(
            this.interpolate(
              this.message(
                slightlyAbove ? "largeMessage" : "farLargeMessage",
                slightlyAbove
                  ? "[size] is still the best starting point for this weight. Because your dog is just above the guide, use the optional chest check if you want extra confidence. Free returns and exchanges make it easy to adjust the fit."
                  : "[size] is the largest available size. This weight is well above the guide, so check the [chest] chest range before ordering. Free returns and exchanges are available if the fit needs adjusting.",
              ),
              { size: row.size, chest: row.chest },
            ),
            row.size,
          );
          return;
        }

        if (recommendation.type === "below") {
          const { row } = recommendation;
          this.recommendedSize = row.size;
          this.canApplyRecommendation = false;
          this.showMessage(
            this.interpolate(
              this.message(
                "smallMessage",
                "[size] is the smallest available size. Because this weight is below the guide, check the [chest] chest range before ordering.",
              ),
              { size: row.size, chest: row.chest },
            ),
            row.size,
          );
          return;
        }

        this.showMessage(
          this.message(
            "outsideMessage",
            "We could not match that weight. Check the size chart or contact us for help.",
          ),
          "",
        );
      }

      showMessage(copy, size) {
        if (!this.result || !this.resultCopy || !this.resultSize) return;
        this.result.hidden = false;
        this.resultSize.textContent = size;
        this.resultCopy.textContent = copy;
        const apply = this.querySelector("[data-size-apply]");
        if (apply) apply.hidden = !this.recommendedSize || !this.canApplyRecommendation;
      }

      applySize() {
        if (!this.recommendedSize || !this.canApplyRecommendation) return;
        const sectionId = this.dataset.sectionId;
        const productForm = sectionId
          ? document.getElementById(`product-form-${sectionId}`)
          : null;
        const scope =
          (sectionId && document.getElementById(`shopify-section-${sectionId}`)) ||
          productForm?.closest(".shopify-section") ||
          productForm ||
          document;
        const radios = Array.from(
          scope.querySelectorAll(
            '.simple-variant-picker input[type="radio"][name^="options["]',
          ),
        );
        const sizeRadio = radios.find((radio) => {
          const optionName = radio.name.toLowerCase();
          return (
            optionName.includes(this.sizeOptionName) &&
            String(radio.value).trim().toUpperCase() === this.recommendedSize.toUpperCase()
          );
        });
        if (!sizeRadio || sizeRadio.disabled) return;

        sizeRadio.checked = true;
        sizeRadio.dispatchEvent(new Event("change", { bubbles: true }));
        this.closest(".size-chart-popup")?.querySelector(".size-chart-popup-close")?.click();
        window.setTimeout(() => {
          scope.querySelector(`label[for="${CSS.escape(sizeRadio.id)}"]`)?.focus?.();
        }, 100);
      }
    },
  );
}
